import { useEffect, useRef } from 'react';

// Drives a smooth combined pan + zoom on a Google map. We animate `moveCamera`
// per frame instead of using `panTo`/`setZoom`, because those only animate when
// the target is within ~one viewport — otherwise they snap instantly.
//
// The flight follows the van Wijk & Nuij (2003) optimal pan-and-zoom path (the
// same maths as `d3.interpolateZoom`), so pan and zoom progress together rather
// than "zoom first, pan last".

const EPSILON = 1e-6;

// van Wijk & Nuij smooth-zoom parameters. rho controls the curvature of the
// flight path; sqrt(2) is the value the paper derives as visually optimal.
const RHO = Math.SQRT2;
const RHO2 = RHO * RHO;
const RHO4 = RHO2 * RHO2;

// ms per unit of the interpolator's intrinsic "distance" S, clamped so short
// hops still feel deliberate and long flights don't drag.
const DURATION_PER_UNIT_MS = 650;
const MIN_DURATION_MS = 400;
const MAX_DURATION_MS = 1200;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const cosh = (x: number) => (Math.exp(x) + Math.exp(-x)) / 2;
const sinh = (x: number) => (Math.exp(x) - Math.exp(-x)) / 2;
const tanh = (x: number) => {
  const e = Math.exp(2 * x);
  return (e - 1) / (e + 1);
};

interface ZoomPathPoint {
  x: number;
  y: number;
  w: number;
}

// Interpolates simultaneously between two camera poses expressed as a world
// point (x, y) and a viewport width `w` (in world units). Returns the
// per-fraction sampler plus the path length `S`, which sets a natural duration.
const buildZoomPath = (from: ZoomPathPoint, to: ZoomPathPoint) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d2 = dx * dx + dy * dy;

  // Pure zoom (no meaningful pan): interpolate the width exponentially.
  if (d2 < 1e-12) {
    const distance = Math.log(to.w / from.w) / RHO;

    return {
      length: Math.abs(distance),
      at: (t: number): ZoomPathPoint => ({
        x: from.x + t * dx,
        y: from.y + t * dy,
        w: from.w * Math.exp(RHO * t * distance),
      }),
    };
  }

  const d1 = Math.sqrt(d2);
  const b0 = (to.w * to.w - from.w * from.w + RHO4 * d2) / (2 * from.w * RHO2 * d1);
  const b1 = (to.w * to.w - from.w * from.w - RHO4 * d2) / (2 * to.w * RHO2 * d1);
  const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0);
  const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1);
  const S = (r1 - r0) / RHO;
  const coshr0 = cosh(r0);

  return {
    length: Math.abs(S),
    at: (t: number): ZoomPathPoint => {
      const s = t * S;
      const u = (from.w / (RHO2 * d1)) * (coshr0 * tanh(RHO * s + r0) - sinh(r0));

      return {
        x: from.x + u * dx,
        y: from.y + u * dy,
        w: (from.w * coshr0) / cosh(RHO * s + r0),
      };
    },
  };
};

export interface CameraAnimationControls {
  /** Whether a fly-to animation is currently in progress. */
  isAnimating: () => boolean;
  /**
   * Marks the map as past its initial framing. Until called, target changes are
   * not animated so the imperative initial `fitBounds` can own the first view.
   */
  markInitialized: () => void;
}

/**
 * Animates the map camera towards `center`/`zoom` whenever they change.
 *
 * The returned controls are meant to be wired into the map's `idle` listener:
 * skip the listener while {@link CameraAnimationControls.isAnimating} is true
 * (so the parent doesn't write the transient mid-flight pose back into state and
 * abort the animation), and call {@link CameraAnimationControls.markInitialized}
 * once the first settled `idle` has fired.
 */
export const useCameraAnimation = (
  map: google.maps.Map,
  center: google.maps.LatLngLiteral,
  zoom: number,
): CameraAnimationControls => {
  const animationFrameRef = useRef<number>(null);
  const isAnimatingRef = useRef(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!map) {
      return;
    }

    const currentCenter = map.getCenter();
    const projection = map.getProjection();
    const fromZoom = map.getZoom();

    // Projection isn't available until the map has rendered once; before that
    // there's nothing user-driven to animate anyway.
    if (!currentCenter || !projection) {
      return;
    }

    const fromLatLng = { lat: currentCenter.lat(), lng: currentCenter.lng() };

    // Already at the target pose — e.g. the idle handler just wrote the map's
    // own center/zoom back into state. Nothing to animate, and skipping avoids
    // a feedback loop.
    if (
      Math.abs(fromLatLng.lat - center.lat) < EPSILON &&
      Math.abs(fromLatLng.lng - center.lng) < EPSILON &&
      Math.abs(fromZoom - zoom) < EPSILON
    ) {
      return;
    }

    // Don't animate the initial framing — the imperative `fitBounds` owns that,
    // and animating here would suppress the first `idle` so clusters never get
    // computed. Only user-driven navigation should animate.
    if (!isInitializedRef.current) {
      return;
    }

    const fromPoint = projection.fromLatLngToPoint(fromLatLng);
    const toPoint = projection.fromLatLngToPoint(center);

    if (!fromPoint || !toPoint) {
      return;
    }

    // Work in world-pixel space so pan and zoom share one curve. `w` is the
    // viewport width expressed in world units (smaller w == more zoomed in).
    const viewportWidth = map.getDiv().offsetWidth || 1;
    const path = buildZoomPath(
      { x: fromPoint.x, y: fromPoint.y, w: viewportWidth / Math.pow(2, fromZoom) },
      { x: toPoint.x, y: toPoint.y, w: viewportWidth / Math.pow(2, zoom) },
    );
    const duration = Math.min(Math.max(path.length * DURATION_PER_UNIT_MS, MIN_DURATION_MS), MAX_DURATION_MS);

    let startTime: number = null;

    const step = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const progress = Math.min((timestamp - startTime) / duration, 1);
      const { x, y, w } = path.at(easeInOutCubic(progress));

      map.moveCamera({
        center: projection.fromPointToLatLng(new google.maps.Point(x, y)),
        zoom: Math.log2(viewportWidth / w),
      });

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        // Settled on the target — let the next (final) `idle` write back through.
        isAnimatingRef.current = false;
      }
    };

    // Cancel any in-flight animation so rapid selections don't fight each other.
    cancelAnimationFrame(animationFrameRef.current);
    isAnimatingRef.current = true;
    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      isAnimatingRef.current = false;
    };
  }, [center.lat, center.lng, map, zoom]);

  return {
    isAnimating: () => isAnimatingRef.current,
    markInitialized: () => {
      isInitializedRef.current = true;
    },
  };
};
